import { useBlockedIntervals } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/BlockedIntervalsContext";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import moment from "moment-timezone";
import { useCallback, useMemo, useState } from "react";
import deepClone2dArray from "utils/deepClone2dArray";
import useBlockedIntervalsIncludingLiveEdits from "../../../../hooks/use-blocked-intervals-including-live-edits";
import {
  BlockedIntervalWithEventType,
  EventContinuedItem,
  EventStartItem,
  FullDayEventSlotItem,
} from "./ScrollableCalendarViewHeader.types";

const createEmptyRow = (rangeLength: number) =>
  new Array(rangeLength).fill({ type: "EMPTY" });

const findOrAddRowWithRoomForEvent = (
  entries: FullDayEventSlotItem[][],
  startDateItemId: number,
  lengthInDays: number,
  rangeLength: number,
) => {
  let firstFreeRowIndex = entries.findIndex((row) => {
    for (let i = startDateItemId; i < startDateItemId + lengthInDays; i++) {
      if (row[i].type !== "EMPTY") {
        return false;
      }
    }
    return true;
  });
  if (firstFreeRowIndex === -1) {
    entries.push(createEmptyRow(rangeLength));
    firstFreeRowIndex = entries.length - 1;
  }
  return firstFreeRowIndex;
};

const fillSlotsWithContinuedEvent = (
  startDateItemId: number,
  lengthInDays: number,
  row: FullDayEventSlotItem[],
) => {
  for (let i = startDateItemId + 1; i < startDateItemId + lengthInDays; i++) {
    row[i] = {
      type: "EVENT_CONTINUED",
      lengthInDays,
      startDateItemId,
    };
  }
};

const replaceAllEventSlotsWithExpandPrompt = (
  correspondingItemInLastRow: EventContinuedItem | EventStartItem,
  row: FullDayEventSlotItem[],
) => {
  const { lengthInDays, startDateItemId } = correspondingItemInLastRow;
  for (let k = startDateItemId; k < startDateItemId + lengthInDays; k++) {
    row[k] = { type: "EXPAND_PROMPT", moreEvents: 1 };
  }
};

const replaceSlotWithExpandPrompt = (
  row: FullDayEventSlotItem[],
  index: number,
) => {
  const currentItem = row[index];
  switch (currentItem.type) {
    case "EVENT_START":
    case "EVENT_CONTINUED": {
      replaceAllEventSlotsWithExpandPrompt(currentItem, row);
      break;
    }
    case "EMPTY": {
      row[index] = { type: "EXPAND_PROMPT", moreEvents: 0 };
      break;
    }
    case "EXPAND_PROMPT":
    default:
      break;
  }
};

const insertOrIncrementExpandPrompt = (
  row: FullDayEventSlotItem[],
  index: number,
) => {
  replaceSlotWithExpandPrompt(row, index);
  const expandPromptItem = row[index];
  if (expandPromptItem.type !== "EXPAND_PROMPT") {
    throw new Error("Wrong cell type in collapsed calendar header");
  }
  expandPromptItem.moreEvents++;
};

const useScrollableCalendarViewHeader = (datesForWeek: {
  [x: string]: Date;
}) => {
  const {
    currentCalendarTimezone,
    setCurrentCalendarTimezone,
    currentTimeRangeType,
    setCurrentTimeRangeType,
    setAnchorTime,
  } = useCalendarTime();
  const { gcalEvents } = useBlockedIntervals();
  const blockedIntervalsIncludingLiveEdits =
    useBlockedIntervalsIncludingLiveEdits();

  const [expanded, setExpanded] = useState(false);

  const daysInRangeSorted = useMemo(
    () => Object.values(datesForWeek).sort((a, b) => a.getTime() - b.getTime()),
    [datesForWeek],
  );

  const rangeStartMoment = useMemo(
    () => moment.tz(daysInRangeSorted[0], currentCalendarTimezone),
    [daysInRangeSorted, currentCalendarTimezone],
  );

  const rangeEndMoment = useMemo(
    () =>
      moment
        .tz(
          daysInRangeSorted[daysInRangeSorted.length - 1],
          currentCalendarTimezone,
        )
        .endOf("day"),
    [daysInRangeSorted, currentCalendarTimezone],
  );

  const blockedIntervalsWithEventTypes = useMemo(
    () => [
      ...blockedIntervalsIncludingLiveEdits.map(
        (el) =>
          ({
            ...el,
            eventType: "BLOCKED_INTERVAL",
          } as BlockedIntervalWithEventType),
      ),
      ...gcalEvents.map(
        (el) =>
          ({
            ...el,
            eventType: "GCAL_EVENT",
          } as BlockedIntervalWithEventType),
      ),
    ],
    [blockedIntervalsIncludingLiveEdits, gcalEvents],
  );

  const fullDayEventsInRange = useMemo(
    () =>
      blockedIntervalsWithEventTypes.filter((event) => {
        const eventStartMoment = moment.tz(
          event.start,
          currentCalendarTimezone,
        );
        const eventEndMoment = moment.tz(event.end, currentCalendarTimezone);

        const isInRange =
          eventStartMoment.isSameOrBefore(rangeEndMoment) &&
          eventEndMoment.isSameOrAfter(rangeStartMoment);
        if (!isInRange) {
          return false;
        }

        const diffDays = eventEndMoment.diff(eventStartMoment, "days", true);
        const isFullDay = diffDays >= 1;

        return isFullDay;
      }),
    [
      blockedIntervalsWithEventTypes,
      rangeEndMoment,
      rangeStartMoment,
      currentCalendarTimezone,
    ],
  );

  const fullDayEventItems: EventStartItem[] = useMemo(
    () =>
      fullDayEventsInRange.map((event) => {
        const eventStartMoment = moment.tz(
          event.start,
          currentCalendarTimezone,
        );
        const eventEndMoment = moment.tz(event.end, currentCalendarTimezone);
        let startOfDay = eventStartMoment.clone().startOf("days");

        const extendsIntoPast = startOfDay.isBefore(rangeStartMoment);
        if (extendsIntoPast) {
          startOfDay = rangeStartMoment.clone();
        }

        const startDateItemId = daysInRangeSorted.findIndex((el) =>
          moment.tz(el, currentCalendarTimezone).isSame(startOfDay, "days"),
        );

        const diffDays = Math.ceil(
          eventEndMoment.diff(startOfDay, "days", true),
        );

        const extendsIntoFuture = eventEndMoment.isAfter(rangeEndMoment);
        const lengthInDays = extendsIntoFuture
          ? daysInRangeSorted.length - startDateItemId
          : diffDays;

        return {
          type: "EVENT_START",
          event,
          startDateItemId,
          lengthInDays,
          extendsIntoPast,
          extendsIntoFuture,
          eventType: event.eventType,
        };
      }),
    [
      daysInRangeSorted,
      fullDayEventsInRange,
      rangeEndMoment,
      rangeStartMoment,
      currentCalendarTimezone,
    ],
  );

  const fullDayEventSlotsExpanded = useMemo(() => {
    const entries: FullDayEventSlotItem[][] = [
      createEmptyRow(daysInRangeSorted.length),
    ];
    for (const item of fullDayEventItems) {
      const { startDateItemId, lengthInDays } = item;
      const firstFreeRowIndex = findOrAddRowWithRoomForEvent(
        entries,
        startDateItemId,
        lengthInDays,
        daysInRangeSorted.length,
      );

      entries[firstFreeRowIndex][startDateItemId] = item;

      fillSlotsWithContinuedEvent(
        startDateItemId,
        lengthInDays,
        entries[firstFreeRowIndex],
      );
    }
    return entries;
  }, [daysInRangeSorted.length, fullDayEventItems]);

  const fullDayEventSlotsCollapsed = useMemo(() => {
    const entries = deepClone2dArray(fullDayEventSlotsExpanded.slice(0, 3));
    if (entries.length >= fullDayEventSlotsExpanded.length) {
      return entries;
    }

    const lastRow = entries[entries.length - 1];
    for (let i = entries.length; i < fullDayEventSlotsExpanded.length; i++) {
      for (let j = 0; j < lastRow.length; j++) {
        if (fullDayEventSlotsExpanded[i][j].type !== "EMPTY") {
          insertOrIncrementExpandPrompt(lastRow, j);
        }
      }
    }
    return entries;
  }, [fullDayEventSlotsExpanded]);

  const showExpandToggle = useMemo(
    () => fullDayEventSlotsExpanded.length > fullDayEventSlotsCollapsed.length,
    [fullDayEventSlotsCollapsed.length, fullDayEventSlotsExpanded.length],
  );

  const fullDayEventSlots = useMemo(
    () => (expanded ? fullDayEventSlotsExpanded : fullDayEventSlotsCollapsed),
    [expanded, fullDayEventSlotsCollapsed, fullDayEventSlotsExpanded],
  );

  const isWeeklyView = useMemo(
    () => currentTimeRangeType === "week",
    [currentTimeRangeType],
  );

  const handleDayLabelClick = useCallback(
    (date: Date) => {
      if (!isWeeklyView) {
        return;
      }

      setCurrentTimeRangeType("day");
      setAnchorTime(date.valueOf());
    },
    [isWeeklyView, setAnchorTime, setCurrentTimeRangeType],
  );

  return {
    timezone: currentCalendarTimezone,
    setTimezone: setCurrentCalendarTimezone,
    expanded,
    setExpanded,
    showExpandToggle,
    fullDayEventSlots,
    isWeeklyView,
    handleDayLabelClick,
  };
};

export default useScrollableCalendarViewHeader;
